import { useCallback, useMemo, useRef, useState } from 'react';
import { useRecipeLibrary } from '../state/RecipeLibraryContext';
import { useHistory } from '../state/HistoryContext';
import { matchRecipes, suitableMatches } from '../lib/matchRecipes.js';
import { searchOnlineRecipes } from '../mocks/searchOnlineRecipes.js';

// Orchestrates history-first discovery so Home stays a screen rather than a
// search engine.
//
// Order of business, and the whole point of Stage 2:
//   1. Rank the user's own library (matchRecipes — pure, instant).
//   2. If anything suitable is there, that IS the answer. Do not go online.
//   3. Only with nothing suitable locally do we search online.
//
// "Try another" walks the same ranked list, so local alternatives are all
// offered before the app reaches for the web, and it wraps once exhausted.

const EMPTY = {
  status: 'idle', // idle | loading | loadingMore | ready | empty | error
  query: '',
  candidates: [],
  index: 0,
  onlineFetched: false,
  // Set once we have looked online and come back with nothing new. Without it,
  // "Try another" searched, found nothing, silently re-showed the same card and
  // then hid its own button — the user saw a dead press and no explanation.
  exhausted: false,
  error: null,
};

export function useRecipeSearch() {
  const { recipes } = useRecipeLibrary();
  const { cookedAt } = useHistory();
  const [state, setState] = useState(EMPTY);

  // Stale-response guard, same pattern the other async screens use.
  const requestRef = useRef(0);

  // History owns "when did I cook this"; the matcher stays pure by being handed
  // the answer rather than reaching for it.
  const entries = useMemo(
    () => recipes.map((recipe) => ({ recipe, lastCookedAt: cookedAt[recipe.id] ?? null })),
    [recipes, cookedAt]
  );
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const runOnline = useCallback(async (query, excludeIds, { append }) => {
    const id = ++requestRef.current;
    setState((prev) => ({ ...prev, status: append ? 'loadingMore' : 'loading', error: null }));

    try {
      const online = await searchOnlineRecipes(query, { excludeIds });
      if (id !== requestRef.current) return;

      setState((prev) => {
        const candidates = append ? [...prev.candidates, ...online] : online;
        if (candidates.length === 0) {
          return { ...prev, status: 'empty', candidates: [], index: 0, onlineFetched: true };
        }
        // Appending lands on the first newly-found option; finding nothing new
        // wraps back to the top of what we already had — and says so, because
        // re-showing the same recipe in silence reads as a broken button.
        const foundMore = online.length > 0;
        const index = append ? (foundMore ? prev.candidates.length : 0) : 0;
        return {
          ...prev,
          status: 'ready',
          candidates,
          index,
          onlineFetched: true,
          exhausted: append && !foundMore,
        };
      });
    } catch (err) {
      if (id !== requestRef.current) return;
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: err?.message || "We couldn't search for that. Try again.",
      }));
    }
  }, []);

  const search = useCallback(
    (rawQuery) => {
      const query = String(rawQuery || '').trim();
      if (!query) return;

      requestRef.current += 1; // drop anything already in flight
      const local = suitableMatches(matchRecipes(query, entriesRef.current));

      if (local.length > 0) {
        setState({ ...EMPTY, status: 'ready', query, candidates: local });
        return;
      }

      setState({ ...EMPTY, status: 'loading', query });
      runOnline(
        query,
        entriesRef.current.map((entry) => entry.recipe.id),
        { append: false }
      );
    },
    [runOnline]
  );

  const tryAnother = useCallback(() => {
    if (state.status !== 'ready' || state.candidates.length === 0) return;

    const next = state.index + 1;
    if (next < state.candidates.length) {
      setState((prev) => ({ ...prev, index: next }));
      return;
    }

    if (!state.onlineFetched) {
      const exclude = [
        ...new Set([
          ...entriesRef.current.map((entry) => entry.recipe.id),
          ...state.candidates.map((candidate) => candidate.recipe.id),
        ]),
      ];
      runOnline(state.query, exclude, { append: true });
      return;
    }

    setState((prev) => ({ ...prev, index: 0 }));
  }, [state, runOnline]);

  const retry = useCallback(() => search(state.query), [search, state.query]);
  const reset = useCallback(() => {
    requestRef.current += 1;
    setState(EMPTY);
  }, []);

  const current = state.candidates[state.index] ?? null;

  return {
    status: state.status,
    error: state.error,
    current,
    query: state.query,
    // We looked online and there was nothing else. A fact the screen has to be
    // able to state, since it is the only outcome the flow cannot show.
    exhausted: state.exhausted,
    // Worth offering "Try another" when there are more in hand, or when we have
    // not yet looked online.
    hasAlternatives: state.candidates.length > 1 || !state.onlineFetched,
    search,
    tryAnother,
    retry,
    reset,
  };
}
